import React, { memo, useMemo, useCallback, useState } from 'react'
import { Box, Button, capitalize, IconButton, Typography } from '@mui/material'
import {
  DataGridPremium,
  GridColDef,
  GridRowId,
  GridRowSelectionModel,
} from '@mui/x-data-grid-premium'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import axios, { pluckData } from 'src/utilities/http-client'
import {
  ContentType,
  downloadFile,
  extensions,
} from 'src/v3/utilities/filesystem'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import { VerificationStatus } from 'src/v3/core/types'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { DownloadIcon, PreviewIcon } from 'src/v3/core/icons'
import { AsyncThunk, AsyncThunkConfig } from '@reduxjs/toolkit'
import {
  selectActiveLinkedService,
  VerificationStatusesResponse,
} from '../../manage.slice'
import { useNotifications } from '../../hooks/use-notifications'
import { licenseTypeDict } from '../../../profile/v4/recertification-cycle'
import { DocumentPreviewDialog } from './document-preview-dialog'

dayjs.extend(utc)

interface VerificationsDataGridProps {
  name: 'nremt' | 'license' | 'credential'
  verifications: VerificationStatusesResponse[]
  fetchVerifications: AsyncThunk<
    VerificationStatusesResponse[],
    string,
    AsyncThunkConfig
  >
}

export const VerificationsDataGrid = memo(
  ({ name, verifications, fetchVerifications }: VerificationsDataGridProps) => {
    let dispatch = useAppDispatch()

    let activeLinkedServiceID = useAppSelector(selectActiveLinkedService)

    let [selectedIDs, setSelectedIDs] = React.useState<GridRowSelectionModel>({
      type: 'include',
      ids: new Set<GridRowId>([]),
    })
    let [activeAction, setActiveAction] = useState<'accept' | 'reject'>()
    let [previewing, setPreviewing] = useState<string>()
    let [downloading, setDownloading] = useState<string>()
    let [viewBlob, setViewBlob] = useState<Blob | null>(null)
    let [viewFileName, setViewFileName] = useState<string>()

    let { showError, NotificationComponent } = useNotifications()

    let idList = Array.from(selectedIDs.ids)
    let checkedIDs =
      selectedIDs.type === 'include'
        ? idList
        : (verifications || [])
            .filter((v) => !idList.includes(v.id))
            .map((v) => v.id)

    let formatExpirationDate = useCallback((dateValue: string | null) => {
      if (!dateValue) return 'N/A'
      return dayjs(dateValue).utc().format('MMM D, YYYY')
    }, [])

    let columns = useMemo<GridColDef[]>(
      () => [
        {
          field: 'name',
          headerName: 'User',
          width: 250,
          flex: 1,
        },
        // Conditionally add Type column for credentials and licenses (not NREMT)
        ...(name !== 'nremt'
          ? [
              {
                field: 'type' as const,
                headerName: 'Type',
                width: 120,
                renderCell: (params: any) =>
                  ({
                    acls: 'ACLS',
                    bls: 'BLS',
                    'ccp-c': 'CCP-C',
                    cfrn: 'CFRN',
                    'fp-c': 'FP-C',
                    nrp: 'NRP',
                    pals: 'PALS',
                    phtls: 'PHTLS',
                  })[(params.value as string) || ''] ||
                  licenseTypeDict[(params.value || '').toLowerCase()] ||
                  params.value,
              },
            ]
          : []),
        // Conditionally add State column for licenses only
        ...(name === 'license'
          ? [
              {
                field: 'state' as const,
                headerName: 'State',
                width: 120,
                renderCell: (params: any) =>
                  params.value ? capitalize(params.value) : '-',
              },
            ]
          : []),
        {
          field: 'number',
          headerName: 'Number',
          width: 150,
          flex: 1,
        },
        {
          field: 'expiration',
          headerName: 'Expiration',
          width: 120,
          renderCell: (params) => formatExpirationDate(params.value),
        },
        {
          field: 'status',
          headerName: 'Status',
          width: 120,
          sortable: false,
          align: 'left',
          headerAlign: 'left',
          renderCell: (params) => {
            let { row } = params

            let { status } = row
            let statusColors = {
              accepted: 'success.main',
              rejected: 'error.main',
              pending: 'warning.main',
            }
            return (
              <Box className="flex items-center justify-start h-full">
                <Typography
                  variant="body2"
                  sx={{
                    color: status
                      ? statusColors[status as VerificationStatus]
                      : 'text.secondary',
                    fontWeight: 500,
                  }}
                >
                  {status ? capitalize(status) : '-'}
                </Typography>
              </Box>
            )
          },
        },
        {
          field: 'actions',
          headerName: 'Actions',
          width: 150,
          renderCell: (params) => {
            let hasImage = !!params.row.imageName?.trim()

            return (
              <>
                <IconButton
                  disabled={
                    !hasImage ||
                    (!!downloading && downloading !== params.row.id)
                  }
                  loading={downloading === params.row.id}
                  onClick={() => {
                    setDownloading(params.row.id)
                    axios
                      .get<string>(
                        name === 'credential'
                          ? `/api/v2/manage/credential/image-url?CredentialID=${params.row.id}`
                          : `/api/manage/${name}/image/${params.row.id}`,
                      )
                      .then(pluckData)
                      .then((response) =>
                        axios
                          .get<Blob>(response, { responseType: 'blob' })
                          .catch(() => {
                            // eslint-disable-next-line fp/no-throw, no-throw-literal
                            throw 'No image uploaded for this item'
                          }),
                      )
                      .then(pluckData)
                      .then((response) =>
                        downloadFile(
                          response,
                          `${name}.${extensions[response.type as ContentType]}`,
                        ),
                      )
                      .catch((err) =>
                        showError(
                          `Image Download Failed: ${err.response?.data || err}`,
                        ),
                      )
                      .finally(() => setDownloading(undefined))
                  }}
                >
                  <DownloadIcon />
                </IconButton>
                <IconButton
                  disabled={
                    !hasImage || (!!previewing && previewing !== params.row.id)
                  }
                  loading={previewing === params.row.id}
                  onClick={() => {
                    setPreviewing(params.row.id)
                    setViewFileName(params.row.imageName)
                    axios
                      .get<string>(
                        name === 'credential'
                          ? `/api/v2/manage/credential/image-url?CredentialID=${params.row.id}`
                          : `/api/manage/${name}/image/${params.row.id}`,
                      )
                      .then(pluckData)
                      .then((response) =>
                        axios
                          .get<Blob>(response, { responseType: 'blob' })
                          .catch(() => {
                            // eslint-disable-next-line fp/no-throw, no-throw-literal
                            throw 'No image uploaded for this item'
                          }),
                      )
                      .then(pluckData)
                      .then((data) => setViewBlob(data))
                      .catch((err) =>
                        showError(
                          err?.response?.data ||
                            err?.message ||
                            'No image available',
                        ),
                      )
                      .finally(() => setPreviewing(undefined))
                  }}
                >
                  <PreviewIcon />
                </IconButton>
              </>
            )
          },
        },
      ],
      [name, downloading, previewing, formatExpirationDate],
    )

    return (
      <>
        <DataGridPremium
          rows={verifications || []}
          columns={columns}
          slots={{
            toolbar: () => (
              <DataGridToolbar fileName={`${name}-verification`} />
            ),
            bottomContainer: () => (
              <Box
                sx={{
                  display: 'flex',
                  borderBottom: '1px solid',
                  borderBottomColor: 'divider',
                }}
              >
                <Button
                  color="success"
                  sx={{ flexGrow: 1 }}
                  loading={activeAction === 'accept'}
                  disabled={!checkedIDs.length || activeAction === 'reject'}
                  onClick={() => {
                    setActiveAction('accept')
                    axios
                      .post(`/api/manage/${name}/bulk-verify`, {
                        linkedServiceID: activeLinkedServiceID,
                        status: 'accepted',
                        items: checkedIDs,
                      })
                      .then(() =>
                        dispatch(fetchVerifications(activeLinkedServiceID)),
                      )
                      .then(() =>
                        setSelectedIDs({
                          type: 'include',
                          ids: new Set<GridRowId>([]),
                        }),
                      )
                      .finally(() => setActiveAction(undefined))
                  }}
                >
                  Accept All
                </Button>
                <Button
                  color="error"
                  sx={{ flexGrow: 1 }}
                  loading={activeAction === 'reject'}
                  disabled={!checkedIDs.length || activeAction === 'accept'}
                  onClick={() => {
                    setActiveAction('reject')
                    axios
                      .post(`/api/manage/${name}/bulk-verify`, {
                        linkedServiceID: activeLinkedServiceID,
                        status: 'rejected',
                        items: checkedIDs,
                      })
                      .then(() =>
                        dispatch(fetchVerifications(activeLinkedServiceID)),
                      )
                      .then(() =>
                        setSelectedIDs({
                          type: 'include',
                          ids: new Set<GridRowId>([]),
                        }),
                      )
                      .finally(() => setActiveAction(undefined))
                  }}
                >
                  Reject All
                </Button>
              </Box>
            ),
          }}
          showToolbar
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          pageSizeOptions={[10, 25, 50]}
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setSelectedIDs(newRowSelectionModel)
          }}
          rowSelectionModel={selectedIDs}
          disableRowSelectionOnClick
          aria-label={`User ${name} verification`}
          getRowId={(row) => row.id}
          sx={{
            backgroundColor: 'background.paper',
            border: 1,
            borderRadius: 1,
            borderColor: 'divider',
            '& .MuiDataGrid-toolbarContainer': {
              borderBottom: 1,
              borderColor: 'divider',
              p: 2,
              backgroundColor: 'background.default',
            },
            '& .MuiDataGrid-cell': {
              borderColor: 'divider',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'background.paperDark',
              borderColor: 'divider',
            },
            '& .MuiDataGrid-footerContainer': {
              borderColor: 'divider',
              backgroundColor: 'background.paperDark',
            },
            '& .MuiDataGrid-row': {
              '&:hover': {
                backgroundColor: 'background.default',
              },
            },
          }}
        />
        <DocumentPreviewDialog
          blob={viewBlob}
          fileName={viewFileName}
          onClose={() => setViewBlob(null)}
        />
        {NotificationComponent}
      </>
    )
  },
)

export default VerificationsDataGrid
